import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Grid, Body } from '@mcafee/pegasus';
import DevicePlatformSelector from './DevicePlatformSelector';
import {
  detectPlatform,
  getSupportedPlatforms,
  getDownloadUrl,
  getPlatformName,
  trackEvent,
} from '../../utils/mobileDownloadConfig';

/**
 * Generic mobile download page component for partners (T-Mobile, Metro, etc.)
 * @param {object} props Configuration object
 * @param {React.Component} props.HeaderComponent - Partner header component
 * @param {React.Component} props.FooterComponent - Partner footer component
 * @param {object} props.config - Partner configuration
 * @param {string} props.config.partnerName - Partner name (T-Mobile, Metro)
 * @param {string} props.config.affiliateId - Partner affiliate ID
 * @param {string} props.config.brandingId - Partner branding ID
 * @param {string} props.config.analyticsId - Google Analytics ID
 * @param {string} props.config.supportedPlatformsEnvVar - Env var name for supported platforms
 * @param {object} props.config.urlConfig - { android: URL, iphone: URL }
 * @param {object} props.config.errorCodeMap - Maps semantic error keys to partner-specific codes
 * @param {object} props.errorMessages - Error message mappings (partner-specific)
 * @param {string} props.pageTitle - Page title
 * @param {string} props.pageSubtitle - Page subtitle
 * @param {string} props.disclaimer - Disclaimer text
 */
export default function MobileDownloadPageBase({
  HeaderComponent,
  FooterComponent,
  config,
  errorMessages,
  pageTitle,
  pageSubtitle,
  disclaimer,
}) {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const supported = useMemo(
    () => getSupportedPlatforms(config.supportedPlatformsEnvVar),
    [config.supportedPlatformsEnvVar]
  );
  const currentPlatform = selectedPlatform || detectedPlatform;

  // Detect platform on component mount and auto-redirect if detected
  useEffect(() => {
    try {
      const detected = detectPlatform();

      if (detected && supported.includes(detected)) {
        setDetectedPlatform(detected);
        trackEvent('platform_detected', config, { platform: detected });

        // Auto-redirect to store URL for detected platform
        const downloadUrl = getDownloadUrl(detected, config.urlConfig);
        if (downloadUrl) {
          trackEvent('download_initiated', config, {
            platform: detected,
            url: downloadUrl,
            installationSource: 'auto',
          });
          window.location.href = downloadUrl;
        }
      }
    } catch (err) {
      console.error('Error detecting platform:', err);
    }

    // Reset loading state in case user navigated back from store URL
    setIsLoading(false);
  }, [config, supported]);

  // Set document title
  useEffect(() => {
    document.title = `${config.partnerName} - Download Security App`;
  }, [config.partnerName]);

  // Listen for page visibility changes (user returns from store)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsLoading(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleDownload = async () => {
    if (!currentPlatform) {
      const errorCode = config.errorCodeMap?.invalid_platform || 'M-001';
      setError(errorMessages[errorCode]);
      return;
    }

    setError(null);

    try {
      const downloadUrl = getDownloadUrl(currentPlatform, config.urlConfig);

      if (!downloadUrl) {
        const errorCode = config.errorCodeMap?.unsupported_platform;
        setError(errorCode ? errorMessages[errorCode] : "Platform not supported");
        trackEvent("download_failed", config, {
          platform: currentPlatform,
          reason: "no_download_url",
        });
        return;
      }

      trackEvent('download_initiated', config, {
        platform: currentPlatform,
        url: downloadUrl,
        installationSource: 'manual',
      });

      // Set loading state and redirect immediately
      setIsLoading(true);
      // Small delay to ensure UI updates before navigation
      setTimeout(() => {
        window.location.href = downloadUrl;
      }, 0);
    } catch (err) {
      console.error("Download error:", err);
      const errorCode = config.errorCodeMap?.unexpected_error;
      setError(errorCode ? errorMessages[errorCode] : "An unexpected error occurred");
      trackEvent("download_error", config, {
        platform: currentPlatform,
        error: err.message,
      });
    }
  };

  const handleChangeDevice = () => {
    setSelectedPlatform(null);
    setError(null);
  };

  return (
    <>
      <div>
        <header className="App-header">
          <HeaderComponent />
        </header>

        <main>
          <section>
            <div className="bg-primary corner-xl bg-size-auto">
              <Grid>
                <Row center="xs">
                  <Col xs={12} sm={12} md={12} lg={12}>
                    <Body level="1">
                      <Card className="py-72" featured>
                        <div className="flex align-items-center">
                          <Grid>
                            {error && (
                              <Row center="xs">
                                <Col xs={12} sm={12} md={10} lg={8}>
                                  <div className="doc-error-message">
                                    <span>{error}</span>
                                  </div>
                                </Col>
                              </Row>
                            )}

                            <Row center="xs">
                              <Col xs={12} sm={12} md={10} lg={8}>
                                <p className="mobile-page-title">
                                  {pageTitle}
                                </p>
                              </Col>
                            </Row>

                            <Row center="xs">
                              <Col xs={12} sm={12} md={10} lg={8}>
                                <p className="mobile-page-subtitle">
                                  {pageSubtitle}
                                </p>
                              </Col>
                            </Row>

                            {detectedPlatform && !selectedPlatform && (
                              <Row center="xs">
                                <Col xs={12} sm={12} md={10} lg={8}>
                                  <p className="mobile-detected-device">
                                    Detected: <strong>{getPlatformName(detectedPlatform)}</strong>
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {selectedPlatform && (
                              <Row center="xs">
                                <Col xs={12} sm={12} md={10} lg={8}>
                                  <p className="mobile-selected-device">
                                    Selected: <strong>{getPlatformName(selectedPlatform)}</strong>
                                    {' '}
                                    <button
                                      onClick={handleChangeDevice}
                                      className="mobile-change-device-btn"
                                    >
                                      Change
                                    </button>
                                  </p>
                                </Col>
                              </Row>
                            )}

                            <Row center="xs">
                              <Col xs={12} sm={12} md={10} lg={8}>
                                <div className="py-32">
                                  <DevicePlatformSelector
                                    selectedPlatform={selectedPlatform}
                                    onPlatformChange={setSelectedPlatform}
                                    detectedPlatform={detectedPlatform}
                                    supportedPlatforms={supported}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row center="xs">
                              <Col xs={12} sm={12} md={10} lg={8}>
                                <div className="py-32">
                                  <input
                                    type="submit"
                                    value={isLoading ? 'Redirecting...' : 'Download Now'}
                                    className="btn-mobile-download"
                                    disabled={!currentPlatform || isLoading}
                                    onClick={handleDownload}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row center="xs">
                              <Col xs={12} sm={12} md={10} lg={8}>
                                <p className="mobile-disclaimer">
                                  {disclaimer}
                                </p>
                              </Col>
                            </Row>
                          </Grid>
                        </div>
                      </Card>
                    </Body>
                  </Col>
                </Row>
              </Grid>
            </div>
          </section>
        </main>

        <br />
        <footer className="App-header pt-24">
          <FooterComponent />
        </footer>
      </div>
    </>
  );
}
